import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from 'styled-components';
import Blog from "../";
import Row from "../../Bootstrap/Row";
import { fetchJSON } from "../../../utilities/fetch";
import Loader from "../../Loader";
import FeaturedArticle from '../FeaturedArticle';

interface IBlogResultsTitleProps {
    count: number;
    category?: string;
    query?: string;
    tag?: string;
}

function BlogResultsTitle(props: IBlogResultsTitleProps) {
    let title = "More Articles";

    if (props.tag || props.query) {
        title = "Search Results";
    }

    if (props.category !== 'All' && !props.tag && !props.query) {
        return null;
    }

    return (
        <div className="col-12 d-flex align-items-baseline">
            <h2 className="text-primary float-left my-0 text-sans">{title}</h2>
            {(props.tag || props.query) &&
                <h5 className="text-muted ml-5 my-0 font-weight-light">
                    {props.count} article{props.count !== 1 && <span>s</span>} matched "{props.tag || props.query}"
                </h5>
            }
        </div>
    )
}

interface IBlogResultsProps {
    category?: string;
    limit: number;
    query?: string;
    tag?: string;
}

export function BlogResults(props: IBlogResultsProps) {
    const [posts, setPosts] = useState([]);
    const [latestFeaturedArticle, setLatestFeaturedArticle] = useState<any>(null);
    const [page, setPage] = useState(1);
    const [isSearching, setIsSearching] = useState(false);
    const [lastPage, setLastPage] = useState(false);
    const [totalArticleCount, setTotalArticleCount] = useState(0);

    const loadMore = useCallback(() => {
            setPage(page + 1);
    }, [page]);

    useEffect(() => {
        try {
            setIsSearching(true);
            const params = [];
            params.push(`q=${props.query || ''}`); // q is required even if blank
            params.push('pt=blog');
            params.push(`page=${page}`);
            params.push(`limit=${props.limit}`);

            if (props.category && !/all/gi.test(props.category)) {
                params.push(`cat=${props.category}`);
            }

            if (props.tag) {
                params.push(`tag=${props.tag}`);
            }
            const cacheBypassParam = `clientCacheBypass=${Math.floor(Date.now() / parseInt(process.env.SOLR_CACHE_INTERVAL_MS || '900001'))}`; // create a param to bypass the cache every 15 minutes
            params.push(cacheBypassParam);

            fetchJSON<any[any]>(`${process.env.GATSBY_SOLR_SEARCH_URL}?${params.join('&')}`)
                .then((res) => {
                    setTotalArticleCount(res.response.numFound);
                    if (res.response.start + props.limit >= res.response.numFound) { // Checks if last apge
                        setLastPage(true)
                    }
                    if (res.response.docs.length > 0) {
                        let firstFeaturedArticle;
                        const additionalArticles = res.response.docs.reduce((acc, curr) => {
                            if (curr.featured_article?.[0] === 'true' && !firstFeaturedArticle) {
                                firstFeaturedArticle = curr;
                            } else {
                                acc.push(curr);
                            }
                            return acc;
                        }, []);

                        if (firstFeaturedArticle) {
                            setLatestFeaturedArticle(firstFeaturedArticle);
                        }

                        setPosts([...posts, ...additionalArticles]);
                    }
                    setIsSearching(false);
                })
                .catch(err => {
                    console.error(err);
                    setIsSearching(false);
                });
        } catch (err) {
            console.error(err);
            setIsSearching(false);
        }
    }, [page]);

    return (
        <div>
            {latestFeaturedArticle &&
                <FeaturedArticle
                    tags={(latestFeaturedArticle.tags || []).join(',')}
                    title={latestFeaturedArticle.blog_title?.[0]}
                    description={latestFeaturedArticle.description?.[0]}
                    href={latestFeaturedArticle.path?.[0]}
                    src={latestFeaturedArticle.thumb_image_src?.[0]}
                    alt={latestFeaturedArticle.thumb_image_alt?.[0]}
                    timestamp={latestFeaturedArticle.timestamp}
                    dateType="timeAgo"
                />
            }
            <BlogResultsTitle category={props.category} query={props.query} tag={props.tag} count={totalArticleCount} />
            <Row className="mt-5">
                {posts.length ?
                    posts.map(post => {
                        return (
                            <div data-test-id="blog-results-item" key={post.id} className="col-xs-12 col-md-6 col-lg-4 pb-6">
                                <Blog
                                    headerAs='small'
                                    href={post.path?.[0]}
                                    title={post.blog_title?.[0]}
                                    timestamp={post.timestamp}
                                    tags={(post.tags || []).join(',')}
                                    alt={post.thumb_image_alt?.[0]}
                                    src={post.thumb_image_src?.[0]}
                                    dateType="date"
                                    isMainArticle={false}
                                />
                            </div>
                        );
                    })
                    :
                    isSearching ? null : 'No articles found'
                }
            </Row>
            {isSearching ?
                <Row className="position-relative w-100 justify-content-center mb-4">
                    <h4 className="text-muted font-weight-light">LOADING MORE</h4>
                    <Loader spinner={isSearching} top={40} width={4} height={4} variant="secondary" />
                </Row>
            :
                !lastPage &&
                    <Row className="position-relative w-100 justify-content-center mb-4">
                        <button className="btn btn-primary" onClick={loadMore}>LOAD MORE</button>
                    </Row>
            }
        </div>
    );
}